import { useState, useCallback } from 'react'
import { observer } from 'mobx-react-lite'
import Styles from './ChatAssistant.module.scss'
import { ChatMessages } from 'components/ChatAssistant/Messages/ChatMessages'
import { ChatInput } from 'components/ChatAssistant/ChatInput/ChatInput'
import { ChatAssistantProps } from './Types'
import { useCreateThread } from 'hooks/queries/chat/useCreateThread'
import { useCreateMessage } from 'hooks/queries/chat/useCreateMessage'
import { ChatMessageContent } from 'api/clients/ChatClient'
import { collectResults } from 'utils/query'
import { useInfiniteMessages } from 'hooks/queries/chat/useInfiniteMessages'
import { AIJobStatus } from 'types'
import { SubmitPayload } from './ChatInput/Types'
import { useAbortMessage } from 'hooks/queries/chat/useAbortMessage'
import { useRootStore } from 'stores/RootStore'
import { ChatTrialEndedMessage } from './Messages/ChatTrialEndedMessage/ChatTrialEndedMessage'
import { channelSupportsCaption } from 'components/Design/utils/channelSupportsCaption'

const ChatAssistant = observer(
  ({ threadId, fileId, setSelectedThreadId, inputPlaceholder, context, isNextGen, channel }: ChatAssistantProps) => {
    const { tierUsageStore } = useRootStore()
    const isTrialExpired = !!tierUsageStore.currentSubscription?.trial_ended
    const showCaptionSuggestion = !channel || channelSupportsCaption(channel)
    const [value, setValue] = useState('')
    const { mutate: createThread, isLoading: isCreatingThread } = useCreateThread()
    const { mutate: createMessage, isLoading: isCreatingMessage } = useCreateMessage()
    const isLoading = isCreatingThread || isCreatingMessage
    const messagesQuery = useInfiniteMessages({ threadId, items: 30 })
    const messages = collectResults(messagesQuery, (data) => data.messages)

    const processingMessage = messages.find(
      (message) => message.status && [AIJobStatus.Pending, AIJobStatus.Started].includes(message.status),
    )
    const isProcessing = !!processingMessage

    const { mutate: abortMessage } = useAbortMessage()

    const handleAbort = useCallback(() => {
      if (processingMessage) {
        abortMessage({ threadId: processingMessage.thread_id, id: processingMessage.id })
      }
    }, [abortMessage, processingMessage])

    const hasDesignContext = !!context && 'design_id' in context

    const handleSubmit = ({ value: text, attachedImage }: SubmitPayload) => {
      const content: ChatMessageContent[] = []

      if (attachedImage) {
        content.push({
          type: 'image',
          image_url: attachedImage.url,
          media_file_key: attachedImage.mediaFileKey,
        })
      }

      content.push({ type: 'text', text })

      if (context) {
        content.push({ type: 'context', context })
      }

      if (!threadId) {
        createThread(
          { content, fileId },
          {
            onSuccess: (data) => {
              setValue('')
              setSelectedThreadId(data.thread.id)
            },
          },
        )
      } else {
        createMessage(
          { threadId, content },
          {
            onSuccess: () => {
              setValue('')
            },
          },
        )
      }
    }

    return (
      <div className={Styles.root}>
        {isTrialExpired ? (
          <ChatTrialEndedMessage />
        ) : (
          <ChatMessages
            threadId={threadId}
            setValue={setValue}
            isNextGen={isNextGen}
            context={context}
            showCaptionSuggestion={showCaptionSuggestion}
          />
        )}
        <div className={Styles.footerContainer}>
          <ChatInput
            value={value}
            onChange={setValue}
            placeholder={inputPlaceholder}
            onSubmit={handleSubmit}
            isDisabled={isLoading || isProcessing || isTrialExpired}
            isLocked={isTrialExpired}
            threadId={threadId}
            fileId={fileId}
            hasDesignContext={hasDesignContext}
            isProcessing={isProcessing}
            onAbort={handleAbort}
            isNextGen={isNextGen}
            showCaptionSuggestion={showCaptionSuggestion}
          />
        </div>
      </div>
    )
  },
)

export { ChatAssistant }
